import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  fetchCrmUserDetail,
  fetchAdminAccount,
  HttpError,
  launchAsUser,
} from "@/services/adminCrm";
import { UserDetailHeader } from "./UserDetailHeader";
import { UserStatsBar } from "./UserStatsBar";
import { UserProfileSection } from "./UserProfileSection";
import { UserJGSSection } from "./UserJGSSection";
import { UserAJGASection } from "./UserAJGASection";
import { UserSubscriptionSection } from "./UserSubscriptionSection";
import { UserMilestonesSection } from "./UserMilestonesSection";
import { UserEmailPrefsSection } from "./UserEmailPrefsSection";
import { UserCoachSection } from "./UserCoachSection";

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["crm-user", userId],
    queryFn: () => fetchCrmUserDetail(userId!),
    enabled: !!userId,
    retry: false,
  });

  const isNotEndUser = error instanceof HttpError && error.status === 404;

  const { data: accountData, isLoading: accountLoading } = useQuery({
    queryKey: ["admin-account", userId],
    queryFn: () => fetchAdminAccount(userId!),
    enabled: isNotEndUser,
    retry: false,
  });

  // Build back link preserving list URL params
  const backPath = location.state?.from || "/recruit-admin/crm";

  if (isLoading || (isNotEndUser && accountLoading)) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isNotEndUser && accountData) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-6 pt-4 pb-2">
          <button
            onClick={() => navigate(backPath)}
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Users
          </button>
        </div>
        <div className="p-6 space-y-4 max-w-lg">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">{accountData.email}</h2>
          </div>
          <div className="rounded-lg border p-4 space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24">Roles</span>
              <span>{accountData.roles.join(", ")}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24">Status</span>
              <span>{accountData.isActive ? "Active" : "Inactive"}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24">Created</span>
              <span>{new Date(accountData.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24">Account ID</span>
              <span className="font-mono text-xs text-muted-foreground">{accountData._id}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(backPath)}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </button>
        <div className="text-red-600">
          {error instanceof Error ? error.message : "Failed to load user details."}
        </div>
      </div>
    );
  }

  function handleMutate() {
    queryClient.invalidateQueries({ queryKey: ["crm-user", userId] });
  }

  async function handleLaunch(path: string) {
    if (!data.user.customer) return toast.error("User has no customer");
    try {
      const url = await launchAsUser(data.user.customer.customerId, data.user.email, path);
      window.open(url, "_blank");
    } catch (err: any) {
      toast.error(`Launch failed: ${err.message}`);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <div className="px-6 pt-4 pb-2">
        <button
          onClick={() => navigate(backPath)}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Users
          {data.user.customer.name && <span> / {data.user.customer.name}</span>}
          <span> / {data.profile.displayName || data.user.email}</span>
        </button>
      </div>

      {/* Sticky header */}
      <UserDetailHeader data={data} />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Stats bar - full width */}
        <UserStatsBar activity={data.activity} onLaunch={handleLaunch} />

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column */}
          <div className="space-y-6">
            <UserProfileSection profile={data.profile} />
            <UserJGSSection jgsData={data.jgsData} linked={data.user.jgsLinked} />
            <UserAJGASection ajgaData={data.ajgaData} linked={data.user.ajgaLinked} />
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {data.user.subscriptionState !== "b2b" && (
              <UserSubscriptionSection
                userId={data.user.userId}
                subscription={data.subscription}
                onMutate={handleMutate}
              />
            )}
            <UserMilestonesSection milestones={data.milestones} />
            <UserEmailPrefsSection emailPreferences={data.emailPreferences} />
          </div>
        </div>

        {/* Coach section - full width */}
        <UserCoachSection coachProfiles={data.coachProfiles} />
      </div>
    </div>
  );
}
